import React from 'react';
import {
  Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  EmptyState, ErrorState, SkeletonRow, Table,
} from '@parsina/framework';
import { History } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { gql } from '../../lib/api';
import { EVENT_PAGE_SIZE } from '../../constants/list';
import { INSURER_CHANGES_QUERY } from './queries';
import { EditorLabel, insurerErrorMessage } from './shared';

/** The most a history read asks for (the contract's ceiling). */
const HISTORY_LIMIT = 200;

/**
 * The service's snapshot keys → the label they read as. Columns that move
 * together read as one: the names map and its two copies, the verifier and
 * the legacy `tamin_code` that follows it.
 */
const FIELD_LABELS = {
  id: 'id',
  country: 'country',
  kind: 'kind',
  names: 'names',
  name_fa: 'names',
  name_en: 'names',
  aliases: 'aliases',
  registry_code: 'registry',
  verify_provider: 'verify',
  tamin_code: 'verify',
  is_active: 'active',
  sort_order: 'sortOrder',
  is_self_pay: 'selfPay',
};

/** A snapshot (JSON), pretty-printed — or verbatim if it is not JSON. */
function pretty(json) {
  if (!json) return '';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

/**
 * Who changed which insurer, when, and the note that says why — newest first,
 * from accounting's append-only insurer history (PM-0246). One insurer's
 * (`insurer`), or the whole catalogue's (`insurer` null), where each row also
 * names its insurer. The rows before and after are behind a toggle.
 *
 * A change has no id of its own in the contract, so a row is keyed by its
 * place in the answer — the list is read whole and never reordered here.
 */
export default function InsurerHistoryDialog({
  open, onOpenChange, insurer = null, name = '', nameOfId,
}) {
  const { t, has, formatDateTime } = useTranslation();
  const [state, setState] = React.useState({ loading: true, error: null, changes: [] });
  const [shown, setShown] = React.useState(EVENT_PAGE_SIZE);
  const [opened, setOpened] = React.useState(null);
  const seq = React.useRef(0);
  const insurerId = insurer?.id ?? null;

  const load = React.useCallback(() => {
    const call = ++seq.current;
    setState({ loading: true, error: null, changes: [] });
    gql(INSURER_CHANGES_QUERY, { insurerId, limit: HISTORY_LIMIT })
      .then((d) => {
        if (call !== seq.current) return;
        if (!Array.isArray(d?.insurerChanges)) throw new Error(t('countries.insurers.history.error'));
        setState({ loading: false, error: null, changes: d.insurerChanges });
      })
      .catch((e) => {
        if (call !== seq.current) return;
        setState({ loading: false, error: insurerErrorMessage(e, t), changes: [] });
      });
  }, [insurerId, t]);

  React.useEffect(() => {
    if (!open) return;
    setShown(EVENT_PAGE_SIZE);
    setOpened(null);
    load();
  }, [open, load]);

  /** The labels of a change's columns, each once, in the order the BFF sorted them. */
  const fieldLabels = (fields) => [...new Set((fields || []).map((f) => {
    const key = `countries.insurers.fields.${FIELD_LABELS[f] || f}`;
    return has(key) ? t(key) : f;
  }))];

  const all = state.changes;
  const rows = all.slice(0, shown).map((c, i) => ({ ...c, key: i }));
  const more = all.length - rows.length;
  const detailRow = rows.find((c) => c.key === opened);

  const columns = [
    {
      key: 'at',
      header: t('countries.history.columns.at'),
      render: (c) => (c.changedAt ? formatDateTime(c.changedAt) : t('common.value.none')),
    },
    ...(insurer ? [] : [{
      key: 'insurer',
      header: t('countries.insurers.history.insurer'),
      render: (c) => {
        const label = nameOfId?.(c.insurerId);
        return (
          <span className="flex min-w-0 items-center gap-2">
            {label ? <span dir="auto" className="truncate">{label}</span> : null}
            <code {...LTR_TEXT} className="font-mono text-xs text-muted-foreground">{c.insurerId}</code>
          </span>
        );
      },
    }]),
    { key: 'by', header: t('countries.history.columns.by'), render: (c) => <EditorLabel by={c.changedBy} /> },
    {
      key: 'fields',
      header: t('countries.insurers.history.fields'),
      render: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.created
            ? <Badge variant="outline">{t('countries.insurers.history.created')}</Badge>
            : fieldLabels(c.changedFields).map((f) => <Badge key={f} variant="outline">{f}</Badge>)}
        </span>
      ),
    },
    {
      key: 'note',
      header: t('countries.history.columns.note'),
      render: (c) => (
        <span dir="auto" className="block max-w-[16rem] truncate" title={c.note || ''}>
          {c.note || t('common.value.none')}
        </span>
      ),
    },
    {
      key: 'detail',
      header: t('countries.history.columns.detail'),
      render: (c) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpened(opened === c.key ? null : c.key)}
          aria-expanded={opened === c.key}
          aria-label={t('countries.history.showRow')}
          data-testid={`insurer-history-toggle-${c.key}`}
        >
          {opened === c.key ? t('common.actions.hide') : t('common.actions.show')}
        </Button>
      ),
    },
  ];

  let body;
  if (state.loading) {
    body = (
      <div role="status" aria-busy="true" aria-label={t('countries.insurers.history.loading')} data-state="loading" data-testid="insurer-history-loading">
        <table className="w-full"><tbody>
          {Array.from({ length: 3 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={i} cols={insurer ? 5 : 6} />
          ))}
        </tbody></table>
      </div>
    );
  } else if (state.error) {
    body = (
      <ErrorState
        title={t('countries.insurers.history.error')}
        description={state.error}
        onRetry={load}
        retryLabel={t('common.actions.retry')}
        data-testid="insurer-history-error"
      />
    );
  } else if (!rows.length) {
    const empty = insurer ? 'empty' : 'allEmpty';
    body = (
      <EmptyState
        icon={<History size={40} strokeWidth={1.5} aria-hidden />}
        title={t(`countries.insurers.history.${empty}.title`)}
        description={t(`countries.insurers.history.${empty}.desc`)}
        data-testid="insurer-history-empty"
      />
    );
  } else {
    body = (
      <div className="flex flex-col gap-2" data-state="content">
        <Table
          dense
          ariaLabel={t('countries.insurers.history.aria')}
          rowKey={(c) => c.key}
          rows={rows}
          rowProps={(c) => ({ 'data-testid': `insurer-history-row-${c.key}` })}
          columns={columns}
          data-testid="insurer-history-table"
        />
        {/* Under the table rather than in a row: a <pre> in a cell fights the columns. */}
        {detailRow ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="insurer-history-detail">
            {[['before', detailRow.before], ['after', detailRow.after]].map(([side, json]) => (
              <div key={side} className="min-w-0">
                <div className="mb-1 text-xs text-muted-foreground">{t(`countries.history.${side}`)}</div>
                <pre {...LTR_TEXT} className="max-h-64 overflow-auto rounded-lg bg-muted p-3 text-[11px] leading-5">
                  {json ? pretty(json) : t('common.value.none')}
                </pre>
              </div>
            ))}
          </div>
        ) : null}
        {more > 0 ? (
          <div>
            <Button variant="outline" size="sm" onClick={() => setShown((n) => n + EVENT_PAGE_SIZE)} data-testid="insurer-history-more">
              {t('countries.history.more')}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" data-testid="insurer-history">
        <DialogHeader>
          <DialogTitle>
            {insurer ? t('countries.insurers.history.title', { name }) : t('countries.insurers.history.allTitle')}
          </DialogTitle>
        </DialogHeader>
        {body}
        <DialogFooter className="mt-6 items-center justify-start gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="insurer-history-close">
            {t('common.actions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
